import React, { useState, useEffect } from "react";
import { createRoot } from "react-dom/client";

interface FirmwareVersion {
  deviceType: string;
  version: string;
  size: number;
  checksum: string;
  uploadedAt: string;
  isLatest: boolean;
  features: string[];
}

interface VersionsResponse {
  deviceType: string;
  versions: FirmwareVersion[];
  latest: string | null;
}

interface DeviceTypesResponse {
  deviceTypes: string[];
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function timeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (seconds < 60) return "just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return formatDate(dateString);
}

const DownloadIcon = () => (
  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
  </svg>
);

const CopyIcon = () => (
  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
  </svg>
);

const PackageIcon = () => (
  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
  </svg>
);

const ServerIcon = () => (
  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
  </svg>
);

const SparkleIcon = () => (
  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
  </svg>
);

const ChipIcon = () => (
  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
  </svg>
);

function App() {
  const [deviceTypes, setDeviceTypes] = useState<string[]>([]);
  const [selectedDeviceType, setSelectedDeviceType] = useState<string | null>(null);
  const [data, setData] = useState<VersionsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedVersion, setCopiedVersion] = useState<string | null>(null);

  useEffect(() => {
    fetchDeviceTypes();
  }, []);

  useEffect(() => {
    if (selectedDeviceType) {
      fetchVersions(selectedDeviceType);
      const interval = setInterval(() => fetchVersions(selectedDeviceType), 30000);
      return () => clearInterval(interval);
    }
  }, [selectedDeviceType]);

  async function fetchDeviceTypes() {
    try {
      const res = await fetch("/firmware/types");
      if (!res.ok) throw new Error("Failed to fetch device types");
      const json: DeviceTypesResponse = await res.json();
      setDeviceTypes(json.deviceTypes);
      if (json.deviceTypes.length > 0 && !selectedDeviceType) {
        setSelectedDeviceType(json.deviceTypes[0]);
      }
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setLoading(false);
    }
  }

  async function fetchVersions(deviceType: string) {
    try {
      const res = await fetch(`/firmware/${deviceType}/versions`);
      if (!res.ok) throw new Error("Failed to fetch versions");
      const json = await res.json();
      setData(json);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error");
    }
  }

  function copyChecksum(version: string, checksum: string) {
    navigator.clipboard.writeText(checksum);
    setCopiedVersion(version);
    setTimeout(() => setCopiedVersion(null), 2000);
  }

  const latestVersion = data?.versions.find((v) => v.isLatest);
  const totalSize = data?.versions.reduce((sum, v) => sum + v.size, 0) ?? 0;

  if (loading) {
    return (
      <div className="container">
        <div className="loading">
          <div className="spinner" />
          <div className="loading-text">Initializing firmware portal...</div>
        </div>
      </div>
    );
  }

  if (error && deviceTypes.length === 0) {
    return (
      <div className="container">
        <header className="header">
          <h1 className="logo">AURA</h1>
          <p className="subtitle">Firmware Registry</p>
        </header>
        <div className="empty-state">
          <div className="empty-icon">&#x26A0;</div>
          <h2 className="empty-title">Connection Error</h2>
          <p className="empty-subtitle">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <header className="header">
        <h1 className="logo">AURA</h1>
        <p className="subtitle">Firmware Registry</p>
      </header>

      {deviceTypes.length > 0 ? (
        <>
          <div className="device-selector">
            <div className="device-selector-label">
              <ChipIcon />
              Device Type
            </div>
            <div className="device-tabs">
              {deviceTypes.map((dt) => (
                <button
                  key={dt}
                  className={`device-tab ${selectedDeviceType === dt ? "active" : ""}`}
                  onClick={() => setSelectedDeviceType(dt)}
                >
                  {dt}
                </button>
              ))}
            </div>
          </div>

          {latestVersion ? (
            <section className="current-version">
              <div className="current-version-label">
                <span className="pulse-dot" />
                Current Release
              </div>
              <div className="version-number">v{latestVersion.version}</div>
              <div className="version-meta">
                <div className="meta-item">
                  <span className="meta-label">Device</span>
                  <span className="meta-value">{latestVersion.deviceType}</span>
                </div>
                <div className="meta-item">
                  <span className="meta-label">Released</span>
                  <span className="meta-value">{formatDate(latestVersion.uploadedAt)}</span>
                </div>
                <div className="meta-item">
                  <span className="meta-label">Size</span>
                  <span className="meta-value">{formatBytes(latestVersion.size)}</span>
                </div>
                <div className="meta-item">
                  <span className="meta-label">SHA-256</span>
                  <span className="meta-value" title={latestVersion.checksum}>
                    {latestVersion.checksum}
                  </span>
                </div>
              </div>
              {latestVersion.features.length > 0 && (
                <div className="features-list">
                  <div className="features-label">
                    <SparkleIcon />
                    What's New
                  </div>
                  <ul className="features">
                    {latestVersion.features.map((feature, i) => (
                      <li key={i} className="feature-item">{feature}</li>
                    ))}
                  </ul>
                </div>
              )}
              <a
                href={`/firmware/${selectedDeviceType}/download?version=${latestVersion.version}`}
                className="download-btn"
              >
                <DownloadIcon />
                Download Latest
              </a>
            </section>
          ) : (
            <div className="empty-state">
              <div className="empty-icon">&#x1F4E6;</div>
              <h2 className="empty-title">No Firmware Available</h2>
              <p className="empty-subtitle">
                Upload firmware for {selectedDeviceType} to get started
              </p>
            </div>
          )}

          {data && data.versions.length > 0 && (
            <>
              <div className="stats-bar">
                <div className="stat-item">
                  <div className="stat-icon">
                    <PackageIcon />
                  </div>
                  <div className="stat-content">
                    <span className="stat-value">{data.versions.length}</span>
                    <span className="stat-label">Total Versions</span>
                  </div>
                </div>
                <div className="stat-item">
                  <div className="stat-icon">
                    <ServerIcon />
                  </div>
                  <div className="stat-content">
                    <span className="stat-value">{formatBytes(totalSize)}</span>
                    <span className="stat-label">Total Storage</span>
                  </div>
                </div>
              </div>

              <section className="version-history">
                <h2 className="section-title">Version History</h2>
                <div className="versions-grid">
                  {data.versions.map((version) => (
                    <div
                      key={version.version}
                      className={`version-card ${version.isLatest ? "latest" : ""}`}
                    >
                      <div className="version-card-header">
                        <div className="version-info">
                          <span className="version-tag">v{version.version}</span>
                          {version.isLatest && <span className="latest-badge">LATEST</span>}
                          <div className="version-details">
                            <span className="version-date">{timeAgo(version.uploadedAt)}</span>
                            <span className="version-size">{formatBytes(version.size)}</span>
                          </div>
                        </div>
                        <div className="version-actions">
                          <button
                            className="action-btn"
                            onClick={() => copyChecksum(version.version, version.checksum)}
                            title={copiedVersion === version.version ? "Copied!" : "Copy SHA-256"}
                          >
                            <CopyIcon />
                          </button>
                          <a
                            href={`/firmware/${selectedDeviceType}/download?version=${version.version}`}
                            className="action-btn"
                            title="Download"
                          >
                            <DownloadIcon />
                          </a>
                        </div>
                      </div>
                      {version.features.length > 0 && (
                        <ul className="version-features">
                          {version.features.map((feature, i) => (
                            <li key={i}>{feature}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            </>
          )}
        </>
      ) : (
        <div className="empty-state">
          <div className="empty-icon">&#x1F4E6;</div>
          <h2 className="empty-title">No Device Types</h2>
          <p className="empty-subtitle">Upload firmware to create your first device type</p>
        </div>
      )}
    </div>
  );
}

createRoot(document.getElementById("root")!).render(<App />);
